/**
 * Gateway Inventory query parameters.
 */
export interface GatewayInventoryParams {
    /**
     * no control of auth handling if set true.
     */
    noAuth?: boolean;
}

/**
 * The mode that the gateway is running as.
 */
export enum GatewayMode {
    /**
     * Indicates the Gateway is running as a service
     */
    Service = 1,

    /**
     * Indicates the Gateway is running as a desktop application
     */
    Desktop,

    /**
     * Indicates the Gateway is running as a UWP application
     */
    App
}

export enum GatewayOperationalMode {
    /**
     * Indicates the Gateway is running in production mode
     */
    Production,

    /**
     * Indicates the Gateway is running in development mode
     */
    Development,

    /**
     * Indicates the Gateway is running in a pipeline for tests.
     */
    Test
}

/**
 * Windows admin center Installation Types
 */
export enum GatewayInstallationType {
    /**
     * Indicating installation type as an azure vm extension
     */
    AzureVmExtension = 'AzureVmExtension',

    /**
     * Standard installation type
     */
    Standard = 'Standard'
}

/**
 * Windows Admin Center connection type
 */
export enum ConnectionsType {
    /**
     * Standard connection type. WAC will show connection list upon connecting
     */
    Standard = 'standard',

    /**
     * Indicates connecting to a cluster on Azure Portal. WAC will lock to Cluster Manager view.
     */
    Cluster = 'cluster',

    /**
     * Indicates connecting to a server on Azure Portal. WAC will lock to Server Manager view.
     */
    Server = 'server'
}

/**
 * Gateway Inventory data interface.
 */
export interface GatewayInventoryData {
    /**
     * The available memory in MBytes.
     */
    availableMemoryMByte: number;

    /**
     * The working set of gateway process.
     */
    gatewayWorkingSetMByte: number;

    /**
     * The total cpu utilization.
     */
    totalCpuUtilizationPercent: number;

    /**
     * The gateway cpu utilization.
     */
    gatewayCpuUtilizationPercent: number;

    /**
     * The gateway version.
     */
    gatewayVersion: string;

    /**
     * The gateway display version.
     */
    gatewayDisplayVersion: string;

    /**
     * The friendly OS name.
     */
    friendlyOsName: string;

    /**
     * The msi installed date.
     */
    installedDate: string;

    /**
     * The logical processor count.
     */
    logicalProcessorCount: number;

    /**
     * The gateway name.
     */
    name: string;

    /**
     * The mode that the gateway is running as.
     */
    mode: GatewayMode;

    /**
     * The operational mode that the gateway is running as
     */
    gatewayOperationalMode: GatewayOperationalMode;

    /**
     * The status of gateway process - if it is running elevated or not.
     */
    isGatewayProcessElevated: boolean;

    /**
     * The installation type of Windows Admin Center
     */
    installationType: string;

    /**
     * The origins allowed by the gateway to host windows admin center in an iframe or other window context
     */
    allowedHostOrigins: string[];
}

/**
 * Gateway Inventory class.
 */
export class GatewayInventory implements GatewayInventoryData {
    /**
     * The available memory in MBytes.
     */
    public availableMemoryMByte: number;

    /**
     * The working set of gateway process.
     */
    public gatewayWorkingSetMByte: number;

    /**
     * The total cpu utilization.
     */
    public totalCpuUtilizationPercent: number;

    /**
     * The gateway cpu utilization.
     */
    public gatewayCpuUtilizationPercent: number;

    /**
     * The gateway version.
     */
    public gatewayVersion: string;

    /**
     * The gateway display version.
     */
    public gatewayDisplayVersion: string;

    /**
     * The friendly OS name.
     */
    public friendlyOsName: string;

    /**
     * The msi installed date.
     */
    public installedDate: string;

    /**
     * The logical processor count.
     */
    public logicalProcessorCount: number;

    /**
     * The gateway name.
     */
    public name: string;

    /**
     * The mode that the gateway is running as.
     */
    public mode: GatewayMode;

    /**
     * The operational mode that the gateway is running as
     */
    public gatewayOperationalMode: GatewayOperationalMode;

    /**
     * The status of gateway process - if it is running elevated or not.
     */
    public isGatewayProcessElevated: boolean;

    /**
     * JWK - JSON Web Key to encrypt password data.
     */
    public jwk: string;

    /**
     * The installation type of Windows Admin Center
     */
    public installationType: string;

    /**
     * The origins allowed by the gateway to host windows admin center in an iframe or other window context
     */
    public allowedHostOrigins: string[];

    /**
     * Initializes a new instance of the GatewayInventory Class.
     *
     * @param data the server inventory recovered data.
     */
    constructor(data?: GatewayInventoryData) {
        if (data) {
            Object.assign(this, data);
        }
    }
}
